import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageCircle, Menu, X, User } from 'lucide-react';
import { getWhatsAppLink } from '../../data/mockData';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';

export const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Inicio' },
    { path: '/schedule', label: 'Horarios' },
    { path: '/memberships', label: 'Membresías' },
    { path: '/coaches', label: 'Coaches' },
    { path: '/results', label: 'Resultados' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100"
      data-testid="header"
    >
      <div className="container-app">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="header-logo"
          >
            <span className="font-heading font-bold text-lg md:text-xl text-[#C9A45C]">
              FitCenter
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium ${isActive(link.path) ? 'active' : ''}`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/account"
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="header-account-link"
            >
              <User className="w-4 h-4" />
              Mi cuenta
            </Link>
            <a
              href={getWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn text-sm"
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="md:hidden p-2 hover:bg-slate-100 rounded-xl transition-colors"
                data-testid="mobile-menu-trigger"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-slate-900" />
                ) : (
                  <Menu className="w-6 h-6 text-slate-900" />
                )}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 p-0">
              <div className="flex flex-col h-full">
                {/* Mobile Menu Header */}
                <div className="p-6 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold text-xl text-[#C9A45C]">
                      FitCenter
                    </span>
                  </div>
                </div>

                {/* Mobile Menu Links */}
                <nav className="flex-1 p-6">
                  <div className="flex flex-col gap-2">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                          isActive(link.path)
                            ? 'bg-[#16263B] text-white'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    <Link
                      to="/account"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                        isActive('/account')
                          ? 'bg-[#16263B] text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid="mobile-nav-account"
                    >
                      Mi cuenta
                    </Link>
                  </div>
                </nav>

                {/* Mobile Menu Footer */}
                <div className="p-6 border-t border-slate-100">
                  <a
                    href={getWhatsAppLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whatsapp-btn w-full justify-center"
                    data-testid="mobile-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Escríbenos por WhatsApp
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
